export const formatCurrency = (amount: number, currency = 'EUR'): string => {
  try {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '€0';
    }

    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    console.warn('Currency formatting error:', error);
    return `${currency} ${amount}`;
  }
};

export const formatDate = (date: string | Date): string => {
  try {
    if (!date) return 'No date';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return new Intl.DateTimeFormat('en-EU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dateObj);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return String(date);
  }
};

export const formatDateTime = (date: string | Date): string => {
  try {
    if (!date) return 'No date';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return new Intl.DateTimeFormat('en-EU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.warn('DateTime formatting error:', error);
    return String(date);
  }
};

export const formatRelativeTime = (date: string | Date): string => {
  try {
    if (!date) return 'No date';

    const now = new Date();
    const targetDate = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(targetDate.getTime())) {
      return 'Invalid date';
    }

    const diffTime = targetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} left`;
    } else if (diffDays === 0) {
      return 'Today';
    } else {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago`;
    }
  } catch (error) {
    console.warn('Relative time formatting error:', error);
    return String(date);
  }
};

export const formatNumber = (num: number): string => {
  try {
    if (typeof num !== 'number' || isNaN(num)) {
      return '0';
    }

    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  } catch (error) {
    console.warn('Number formatting error:', error);
    return String(num);
  }
};

export const truncateText = (text: string, maxLength: number): string => {
  try {
    if (!text || typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  } catch (error) {
    console.warn('Text truncation error:', error);
    return String(text);
  }
};

export const formatFileSize = (bytes: number): string => {
  try {
    if (typeof bytes !== 'number' || isNaN(bytes) || bytes <= 0) {
      return '0 KB';
    }

    const MB = 1024 * 1024;
    const KB = 1024;

    if (bytes >= MB) {
      return (bytes / MB).toFixed(1) + ' MB';
    } else if (bytes >= KB) {
      return (bytes / KB).toFixed(0) + ' KB';
    } else {
      return '1 KB';
    }
  } catch (error) {
    console.warn('File size formatting error:', error);
    return String(bytes) + ' bytes';
  }
};

export const getFileTypeFromMimeType = (mimeType: string): string => {
  try {
    if (!mimeType || typeof mimeType !== 'string') {
      return 'FILE';
    }

    const mimeTypeMap: Record<string, string> = {
      'application/pdf': 'PDF',
      'image/jpeg': 'JPG',
      'image/jpg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WEBP',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'text/plain': 'TXT',
      'text/csv': 'CSV',
      'application/zip': 'ZIP',
      'application/x-rar-compressed': 'RAR',
      'application/json': 'JSON',
      'text/xml': 'XML',
      'application/xml': 'XML',
    };

    const fileType = mimeTypeMap[mimeType.toLowerCase()];
    if (fileType) {
      return fileType;
    }

    // Extract from mime type if not in map
    const [type, subtype] = mimeType.split('/');
    if (subtype) {
      return subtype.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    return type ? type.toUpperCase() : 'FILE';
  } catch (error) {
    console.warn('File type formatting error:', error);
    return 'FILE';
  }
};
